import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Select,
  Stack,
  TextField,
  Typography,
  Checkbox,
} from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import MenuItem from "@mui/material/MenuItem";
import { FeedbackEditor } from "./FeedbackEditor";
import { useApiClient } from "../../../api";
import GeneralLoading from "../../../shared-components/Loading";
import { AnswerEditor } from "./AnswerEditor";
import { useAnswerViewTracking } from "../hooks/useAnswerViewTracking";
import Grid from "@mui/material/Grid";
import StatBox from "../../../shared-components/StatBox";
import {
  formatWeekDifference,
  getWeekDiffColor,
} from "../../../utils/formatters";
import { EditExtractedTextDialog } from "./EditExtractedTextDialog";
import { enqueueSnackbar } from "notistack";
import { isHolisticMarking } from "../../../utils/preferences";
import { PastDeadlineChip } from "../states/PastDeadlineChip";
import { QuestionViewCard } from "../../question/components/QuestionViewCard";
import { RetryExtractionButton } from "./RetryExtractionButton";
import { AnswerEditor as AnswerReviewEditor } from "./answer-editor/AnswerEditor";

export const StudentPerformanceSummaryStats = ({
  mark,
  availableMarks,
  lastAssignmentScore = null,
  question = false,
  noAnswers = false,
  markedHolistically = false,
  onHolisticMarkChange = () => {},
  studentView = false,
  hideTitle = false,
}) => {
  const percentage = Math.round((mark / availableMarks) * 100);
  const statGridSize = question && !studentView ? 12 : 6;
  return (
    <Box sx={{ mt: 2 }}>
      {!question && !hideTitle && (
        <Typography variant={"h3"}>Assignment Performance</Typography>
      )}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid size={statGridSize}>
          <StatBox
            title={
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {question ? "Question Score" : "Assignment Score"}
                {markedHolistically && (
                  <Chip label="Holistic Marking" size="small" />
                )}
              </div>
            }
            stat={
              markedHolistically && !studentView ? (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <TextField
                    type="number"
                    size="small"
                    value={mark ?? ""}
                    onChange={(e) =>
                      onHolisticMarkChange(Number(e.target.value))
                    }
                    sx={{
                      width: 70, // Slightly wider to accommodate double digits comfortably
                      "& .MuiInputBase-input": {
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                      },
                    }}
                    inputProps={{
                      min: 0,
                      max: availableMarks ?? 0,
                    }}
                  />
                  <Box
                    sx={{
                      width: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1rem",
                      height: 40,
                      color: "text.secondary",
                    }}
                  >
                    /{availableMarks}
                  </Box>
                </Box>
              ) : (
                `${mark}/${availableMarks}`
              )
            }
            footer={<LinearProgress variant="determinate" value={percentage} />}
            noDataMessage={
              noAnswers &&
              (studentView
                ? "You have not submitted any answers yet"
                : "The student has not submitted any answers yet")
            }
          />
        </Grid>
        <Grid size={statGridSize}>
          <StatBox
            title={question ? "Question Percentage" : "Assignment Percentage"}
            stat={`${percentage}%`}
            footer={
              !question &&
              formatWeekDifference({
                current: percentage,
                previous: lastAssignmentScore,
                successfulComparison: lastAssignmentScore !== null,
              })
            }
            footerColor={getWeekDiffColor({
              current: percentage,
              previous: lastAssignmentScore,
              successfulComparison: lastAssignmentScore !== null,
            })}
            noDataMessage={
              noAnswers &&
              (studentView
                ? "You have not submitted any answers yet"
                : "The student has not submitted any answers yet")
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};

const AssignmentQuestionControls = ({
  selectedAssignmentQuestion,
  setSelectedAssignmentQuestion,
  assignmentQuestions,
  viewed,
}) => {
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        {assignmentQuestions?.length > 1 ? (
          <Select
            value={selectedAssignmentQuestion}
            onChange={(e) => setSelectedAssignmentQuestion(e.target.value)}
            size="small"
          >
            {assignmentQuestions.map((q) => (
              <MenuItem key={q.id} value={q}>
                {`Question ${q.identifier}`}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <Typography variant="subtitle1" fontWeight="medium">
              Question
            </Typography>
            <Chip
              label={viewed ? "Viewed" : "Not Viewed"}
              color={viewed ? "success" : "default"}
            />
          </Stack>
        )}
      </Stack>

      <QuestionViewCard
        question={selectedAssignmentQuestion.question}
        identifier={selectedAssignmentQuestion?.identifier}
        collapsibleMenu
        hideOptions
        sx={{ mt: 2, mb: 0 }}
      />
    </Box>
  );
};

const AssignmentQuestionFeedbackEditor = ({
  studentAnswer,
  feedback,
  stretch,
  setStudentAnswer,
}) => {
  const [studentFeedback, setStudentFeedback] = useState({});

  const ApiClient = useApiClient();

  useEffect(() => {
    setStudentFeedback(feedback);
  }, [feedback]);

  const updateFeedback = async ({ areasToImprove, specificFeedback }) => {
    try {
      const response = await ApiClient.assignment.updateTeacherFeedback({
        feedback: feedback.id,
        areasToImprove,
        specificFeedback,
      });
      if (response) {
        setStudentFeedback(response.feedback);
        setStudentAnswer((prev) => ({
          ...prev,
          teacherFeedback: response.feedback,
        }));
      }
    } catch (error) {
      console.error("Failed to update Feedback", error);
    }
  };
  return (
    <FeedbackEditor
      student
      excludeTitles
      savedFeedback={studentFeedback}
      onSave={updateFeedback}
      stretch={stretch}
      canonicalEntityInsights={studentAnswer.canonicalEntityInsights}
      answer={studentAnswer}
    />
  );
};

const AnalysingSpinner = ({ message }) => {
  return (
    <Box sx={{ mt: 5 }}>
      <GeneralLoading message={message} />
    </Box>
  );
};

export const AnswerViewer = ({
  studentAnswer,
  setStudentAnswer,
  onSuccessfulSubmit,
  onMarkChange,
  onEditClicked,
  editing,
  onEditCancelled,
  assignmentPreferences = null,
  studentView = false,
  onDirtyChange,
  disableSubmit = false,
  showFeedback = false,
  isModerator = false,
  assignmentQuestion,
  student,
  onOpenExtraction,
}) => {
  useAnswerViewTracking(studentAnswer);

  if (
    studentAnswer?.status === "PENDING" ||
    studentAnswer?.status === "BEING_MARKED"
  ) {
    return <AnalysingSpinner message={"The answer is being marked"} />;
  }
  if (studentAnswer?.status === "EXTRACTING_TEXT") {
    return <AnalysingSpinner message={"The answer is being extracted"} />;
  }
  if (studentAnswer?.status === "EXTRACTION_FAILED") {
    const handleRetrySuccess = async () => {
      setStudentAnswer((prev) =>
        prev ? { ...prev, status: "PENDING" } : prev,
      );
      await onSuccessfulSubmit?.();
    };
    return (
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Stack direction="column" spacing={2}>
            <Typography variant="h5">Text extraction failed</Typography>
            <Typography variant="body1">
              We couldn't extract text from the uploaded file. You can edit the
              answer manually below.
            </Typography>
            <Stack direction="row" spacing={2}>
              {disableSubmit ? (
                <PastDeadlineChip />
              ) : (
                <Button variant="contained" onClick={onEditClicked}>
                  Edit Answer
                </Button>
              )}
              <RetryExtractionButton
                answer={studentAnswer}
                onSuccess={handleRetrySuccess}
                disabled={disableSubmit}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  }
  if (
    studentAnswer?.status === "SUBMITTED" ||
    !studentAnswer ||
    editing ||
    (studentView && !showFeedback)
  ) {
    return (
      <AnswerEditor
        key={`${assignmentQuestion?.id}-${student?.id || studentAnswer?.student?.id}`}
        studentView={studentView}
        onDirtyChange={onDirtyChange}
        answer={studentAnswer}
        assignmentQuestion={assignmentQuestion}
        student={student}
        onSuccessfulSubmit={onSuccessfulSubmit}
        onCancelEditing={() => onEditCancelled(false)}
        editingExisting={editing}
        disableSubmit={disableSubmit}
      />
    );
  }
  if (studentAnswer?.status === "MARKED") {
    return (
      <AnswerReviewEditor
        studentAnswer={studentAnswer}
        setStudentAnswer={setStudentAnswer}
        onMarkChange={onMarkChange}
        assignmentPreferences={assignmentPreferences}
        mode={
          isModerator
            ? "moderator-review"
            : studentView
              ? "student-review"
              : "teacher-review"
        }
        disableSubmit={disableSubmit}
        onOpenExtraction={onOpenExtraction}
      />
    );
  }
};

const AnswerApproveToggle = ({ studentAnswer, setStudentAnswer }) => {
  const ApiClient = useApiClient();
  const [approvingToggleLocked, setApprovingToggleLocked] = useState(false);

  const handleToggleApprove = async (checked) => {
    if (!studentAnswer?.id || approvingToggleLocked) return;
    setApprovingToggleLocked(true);
    try {
      const res = await ApiClient.assignment.approveAnswerFeedback({
        answer: studentAnswer.id,
        approved: checked,
      });
      if (res?.success) {
        enqueueSnackbar(
          checked ? "Approved mark and feedback" : "Removed approval",
          { variant: "success", autoHideDuration: 3000 },
        );
        setStudentAnswer?.((prev) => {
          if (!prev?.teacherFeedback) return prev;
          return {
            ...prev,
            teacherFeedback: {
              ...prev.teacherFeedback,
              approvedAt: checked ? new Date().toISOString() : null,
            },
          };
        });
      } else {
        enqueueSnackbar(res?.message || "Failed to update approval state", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (err) {
      console.error("Failed to update approval state", err);
      enqueueSnackbar("Failed to update approval state", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
    setTimeout(() => setApprovingToggleLocked(false), 1500);
  };
  const approved = Boolean(studentAnswer?.teacherFeedback?.approvedAt);
  return (
    <Box
      sx={{
        mt: 1,
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 1.5,
          minWidth: 220,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          {approvingToggleLocked ? (
            <GeneralLoading
              size={20}
              direction={"row"}
              message={approved ? "Approving" : "Removing"}
            />
          ) : (
            <>
              <Checkbox
                checked={approved}
                onChange={(e) => handleToggleApprove(e.target.checked)}
                disabled={approvingToggleLocked}
                sx={{ mt: 0.25 }}
              />
              <Stack direction="column" spacing={0.25}>
                <Typography
                  fontWeight="bold"
                  color={
                    approvingToggleLocked ? "text.disabled" : "text.primary"
                  }
                >
                  Approve
                </Typography>
                {studentAnswer?.teacherFeedback?.approvedAt && (
                  <Typography variant="body2" color="text.secondary">
                    Approved{" "}
                    {formatDistanceToNow(
                      new Date(studentAnswer.teacherFeedback.approvedAt),
                      { addSuffix: true },
                    )}
                  </Typography>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export const AssignmentQuestionAnswerViewer = ({
  assignmentQuestions,
  studentAnswers,
  onSuccessfulSubmit,
  onMarkChange,
  mark,
  lastAssignmentScore,
  studentAnswer,
  student,
  setStudentAnswer,
  selectedAssignmentQuestion,
  setSelectedAssignmentQuestion,
  assignmentPreferences = null,

  isModerator = false,

  onHolisticMarkChange,
  disableSubmit = false,
}) => {
  const ApiClient = useApiClient();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editDialogText, setEditDialogText] = useState("");
  const [savingEditedAnswer, setSavingEditedAnswer] = useState(false);
  const [editing, setEditing] = useState(false);
  const originalEditDialogTextRef = useRef("");

  const handleOpenEditExtractedDialog = () => {
    const original = studentAnswer?.answer || "";
    originalEditDialogTextRef.current = original;
    setEditDialogText(original);
    setEditDialogOpen(true);
  };

  const handleEditClicked = () => {
    if (studentAnswer.handwrittenAnswerFile) {
      handleOpenEditExtractedDialog();
    } else {
      setEditing(true);
    }
  };
  const handleSaveEditExtractedDialog = async () => {
    if (!studentAnswer) return;

    if (
      (originalEditDialogTextRef.current || "").trim() ===
      (editDialogText || "").trim()
    ) {
      setEditDialogOpen(false);
      return;
    }
    try {
      setSavingEditedAnswer(true);
      const response = await ApiClient.assignment.submitAnswer({
        assignmentQuestion: studentAnswer.assignmentQuestion.id,
        student: studentAnswer.student.id,
        answer: editDialogText,
      });
      if (response?.success) {
        enqueueSnackbar("Answer saved", {
          variant: "success",
          autoHideDuration: 3000,
        });
        setStudentAnswer((prev) =>
          prev ? { ...prev, answer: editDialogText } : prev,
        );
        setEditDialogOpen(false);
      } else {
        enqueueSnackbar(response?.message || "Failed to save answer", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to save answer", error);
      enqueueSnackbar("Failed to save answer", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setSavingEditedAnswer(false);
    }
  };

  const handleRetrySuccess = async () => {
    setEditDialogOpen(false);
    setStudentAnswer((previous) =>
      previous ? { ...previous, status: "PENDING" } : previous,
    );
    await onSuccessfulSubmit?.();
  };

  return (
    <Box>
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <AssignmentQuestionControls
            selectedAssignmentQuestion={selectedAssignmentQuestion}
            setSelectedAssignmentQuestion={setSelectedAssignmentQuestion}
            assignmentQuestions={assignmentQuestions}
            viewed={studentAnswer?.viewed}
          />
          {studentAnswers.length > 0 && (
            <Grid container spacing={2} alignItems="stretch">
              {assignmentQuestions?.length > 1 && (
                <Grid
                  size={4}
                  sx={{ display: "flex", flexDirection: "column" }}
                >
                  <StudentPerformanceSummaryStats
                    question
                    mark={mark}
                    availableMarks={selectedAssignmentQuestion?.availableMarks}
                    lastAssignmentScore={lastAssignmentScore}
                    markedHolistically={isHolisticMarking(
                      assignmentPreferences,
                    )}
                    onHolisticMarkChange={onHolisticMarkChange}
                  />
                </Grid>
              )}
              <Grid
                size={assignmentQuestions?.length > 1 ? 8 : 12}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                {studentAnswer?.status === "MARKED" && (
                  <>
                    <AssignmentQuestionFeedbackEditor
                      studentAnswer={studentAnswer}
                      feedback={studentAnswer?.teacherFeedback}
                      stretch={assignmentQuestions?.length > 1}
                      setStudentAnswer={setStudentAnswer}
                    />
                  </>
                )}
              </Grid>
            </Grid>
          )}
          {studentAnswer?.status === "MARKED" && !isModerator && (
            <AnswerApproveToggle
              studentAnswer={studentAnswer}
              setStudentAnswer={setStudentAnswer}
            />
          )}
        </CardContent>
      </Card>
      <EditExtractedTextDialog
        open={editDialogOpen}
        setOpen={setEditDialogOpen}
        chosenAnswer={studentAnswer}
        answer={editDialogText}
        setAnswer={setEditDialogText}
        onSave={handleSaveEditExtractedDialog}
        saving={savingEditedAnswer}
        disableSave={
          (originalEditDialogTextRef.current || "").trim() ===
          (editDialogText || "").trim()
        }
        onRetrySuccess={handleRetrySuccess}
        disableRetry={disableSubmit}
        readOnly={disableSubmit}
      />
      <Box sx={{ mt: 2 }}>
        <AnswerViewer
          studentAnswer={studentAnswer}
          onSuccessfulSubmit={onSuccessfulSubmit}
          setStudentAnswer={setStudentAnswer}
          onMarkChange={onMarkChange}
          onEditClicked={handleEditClicked}
          onEditCancelled={() => setEditing(false)}
          assignmentPreferences={assignmentPreferences}
          editing={editing}
          isModerator={isModerator}
          assignmentQuestion={selectedAssignmentQuestion}
          student={student}
          disableSubmit={disableSubmit}
          onOpenExtraction={handleOpenEditExtractedDialog}
        />
      </Box>
    </Box>
  );
};
